'use strict';

var toastMessageHelper = require('../../utils/toastMessageHelper');

/**
 * Contact Us Form Validation and Submission Script
 *
 * This script handles real-time validation and submission of a contact form.
 * It validates user inputs such as first name, last name, email, mobile number, and message.
 * On successful validation, it displays a success message and resets the form.
 */

// Cache DOM elements
const $form = $('#contactUsForm');
const $firstName = $('#firstName');
const $lastName = $('#lastName');
const $email = $('#email');
const $mobile = $('#mobile');
const $message = $('#message');

/**
 * Displays an error message for the specified input field.
 * @param {$} $field - The jQuery-wrapped input field element.
 * @param {string} message - The error message to display.
 */
function showError($field, message) {
    $field.addClass('is-invalid');
    $field.siblings('.invalid-feedback').text(message).removeClass('d-none').addClass('d-block');
}

/**
 * Clears the error message from the specified input field.
 * @param {$} $field - The jQuery-wrapped input field element.
 */
function clearError($field) {
    $field.removeClass('is-invalid');
    $field.siblings('.invalid-feedback').text('').addClass('d-none').removeClass('d-block');
}

/**
 * Adds an "is-invalid" CSS class to the customer title dropdown section
 * within the first-name form group to reflect an invalid state in the UI.
 *
 * @function addInvalidToCustomerTitle
 * @requires jQuery
 * @returns {void}
 */
function addInvalidToCustomerTitle() {
    $('.first-name-group .custom-drop-down-section').addClass('is-invalid');
}

/**
 * Removes the "is-invalid" validation class from the customer title dropdown section
 * within the first-name group, clearing any displayed invalid state.
 *
 * @function removeInvalidFromCustomerTitle
 * @returns {void}
 */
function removeInvalidFromCustomerTitle() {
    $('.first-name-group .custom-drop-down-section').removeClass('is-invalid');
}

/**
 * Adds an "is-invalid" CSS class to the mobile dialing code custom dropdown section
 * within the contact us form, marking it visually as invalid.
 *
 * @function addInvalidToMobileDiaCode
 * @returns {void}
 */
function addInvalidToMobileDiaCode() {
    $('.contactusform-mobile-dialing-code .custom-drop-down-section').addClass('is-invalid');
}

/**
 * Removes the `is-invalid` validation state from the mobile dialing code custom dropdown
 * within the Contact Us form.
 *
 * Specifically targets `.contactusform-mobile-dialing-code .custom-drop-down-section`
 * and removes the `is-invalid` CSS class via jQuery.
 *
 * @function removeInvalidFromMobileDiaCode
 * @returns {void}
 */
function removeInvalidFromMobileDiaCode() {
    $('.contactusform-mobile-dialing-code .custom-drop-down-section').removeClass('is-invalid');
}

/**
 * Validates a name field (First/Last name).
 *
 * Rules:
 * - Required (non-empty after `trim()`)
 * - Letters only, with optional single spaces between words
 *   (no leading/trailing space; multiple consecutive spaces are invalid)
 *
 * Error messaging:
 * - Uses `data-missing-error` when empty
 * - Uses `data-invalid-error` when format is invalid
 *
 * Side effects:
 * - When validating the first-name input, toggles `is-invalid` on the customer-title dropdown
 *
 * @param {$} $field - jQuery-wrapped name input field.
 * @param {string} fieldLabel - Field label (currently unused; kept for API consistency).
 * @returns {boolean} True when valid; otherwise false.
 */
function validateName($field, fieldLabel) {
    var value = $field.val().trim(); // trim only for validation
    var nameMissingError = $field.data('missing-error');
    var nameInvalidError = $field.data('invalid-error');

    if (value === '') {
        if ($field.is($firstName) || fieldLabel === 'First') {
            addInvalidToCustomerTitle();
        }
        showError($field, nameMissingError);
        return false;
    }

    // Allow letters and single spaces between words, but not start or end with space
    if (!/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(value)) {
        if ($field.is($firstName) || fieldLabel === 'First') {
            addInvalidToCustomerTitle();
        }
        showError($field, nameInvalidError);
        return false;
    }

    if ($field.is($firstName) || fieldLabel === 'First') {
        removeInvalidFromCustomerTitle();
    }

    clearError($field);
    return true;
}

/**
 * Validates the email field.
 *
 * Rules:
 * - Required (non-empty after `trim()`)
 * - Must match a simplified email pattern: `local@domain.tld`
 *   (single dot in the domain, TLD length 2–6)
 *
 * Error messaging:
 * - Uses `data-missing-error` when empty
 * - Uses `data-invalid-error` when format is invalid
 *
 * @param {$} $field - jQuery-wrapped email input field.
 * @returns {boolean} True when valid; otherwise false.
 */
function validateEmail($field) {
    var value = $field.val().trim();
    // const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    var emailPattern = /^[^\s@]+@[A-Za-z0-9-]+\.[A-Za-z]{2,6}$/;

    if (value === '') {
        var emailMissingError = $field.data('missing-error');
        showError($field, emailMissingError);
        return false;
    }

    if (!emailPattern.test(value)) {
        var emailInvalidError = $field.data('invalid-error');
        showError($field, emailInvalidError);
        return false;
    }

    clearError($field);
    return true;
}

/**
 * Validates the mobile number field.
 *
 * Rules:
 * - Required (non-empty)
 * - Digits only (non-digits are stripped and treated as invalid)
 * - Length must match the input's `maxlength` attribute (defaults to 10)
 * - If the selected dialing code is `+91`, the number must start with 6–9
 *
 * Side effects:
 * - Adds/removes `is-invalid` on the mobile dialing-code dropdown container
 * - May mutate the input value (strip non-digits / clear on `+91` prefix failure)
 *
 * @param {$} $field - jQuery-wrapped mobile input field.
 * @returns {boolean} True when valid; otherwise false.
 */
function validateMobile($field) {
    let value = $field.val().trim();
    var maxLength = parseInt($field.attr('maxlength'), 10) || 10;

    var mobileMissingError = $field.data('missing-error');
    var mobileInvalidError = $field.data('invalid-error');
    var indiaInvalidError = $field.data('india-invalid-error');

    var countryCode = $('#contactUsForm .contactusform-mobile-dialing-code .selected-value')
        .text()
        .trim();

    // Empty check
    if (value === '') {
        showError($field, mobileMissingError);
        addInvalidToMobileDiaCode();
        return false;
    }

    // Allow only digits (safety check)
    if (!/^\d+$/.test(value)) {
        $field.val(value.replace(/\D/g, ''));
        showError($field, mobileInvalidError);
        addInvalidToMobileDiaCode();
        return false;
    }

    // Country-specific rule for +91
    if (countryCode === '+91' && !/^[6-9]/.test(value)) {
        $field.val('');
        showError($field, indiaInvalidError);
        addInvalidToMobileDiaCode();
        return false;
    }

    // Length check
    if (value.length !== maxLength) {
        showError($field, mobileInvalidError);
        addInvalidToMobileDiaCode();
        return false;
    }

    clearError($field);
    removeInvalidFromMobileDiaCode();
    return true;
}

/**
 * Validates the message field.
 *
 * Rules:
 * - Required (non-empty after `trim()`)
 * - Maximum 100 words (words are split by whitespace)
 *
 * Error messaging:
 * - Uses `data-missing-error` when empty
 * - Uses `data-invalid-error` when word count exceeds the limit
 *
 * @param {$} $field - jQuery-wrapped message textarea.
 * @returns {boolean} True when valid; otherwise false.
 */
function validateMessage($field) {
    const value = $field.val().trim();

    // Check for empty value
    if (value === '') {
        const missingError = $field.data('missing-error');
        showError($field, missingError);
        return false;
    }

    // Count words
    const wordCount = value.split(/\s+/).filter(Boolean).length;

    // Check for max word limit
    if (wordCount > 100) {
        const invalidError = $field.data('invalid-error');
        showError($field, invalidError);
        return false;
    }

    clearError($field);
    return true;
}

/**
 * Resets the customer title dropdown in the contact form.
 *
 * - If a customerTitle is provided, selects the matching option.
 * - If not provided or no match is found, selects the first option.
 * - Updates the visible selected value and the hidden input field.
 * - Makes the dropdown non-editable when a title is explicitly passed.
 *
 * @function resetCustomerTitle
 * @param {string} [customerTitle] - Optional customer title to preselect (e.g., "Mr", "Mrs", "Ms").
 * @returns {void}
 */
function resetCustomerTitle(customerTitle) {
    var contatusFormCustomerTitle = customerTitle || ''; //
    var $nameSection = $('.contactusform-customer-title-dropdown');
    if (!$nameSection.length) return;

    var $dropdown = $nameSection.find('.custom-drop-down-section');
    if (!$dropdown.length) return;

    var $options = $dropdown.find('.custom-option-value');
    if (!$options.length) return;

    var $display = $dropdown.find('.selected-value').first();
    var $hiddenInput = $nameSection.find('input[name="customerTitle"]');

    var $targetOption;

    // If contatusFormCustomerTitle is passed → select matching option
    if (contatusFormCustomerTitle) {
        $targetOption = $options.filter(function () {
            var title = $(this).attr('data-title') || $(this).text().trim();
            return title === contatusFormCustomerTitle;
        }).first();
    }

    // If NOT passed or not found → select FIRST <li>
    if (!$targetOption || !$targetOption.length) {
        $targetOption = $options.first();
    }

    var nextTitle = $targetOption.attr('data-title') || $targetOption.text().trim();
    var nextCode = $targetOption.attr('data-code');

    // Reset selection
    $options.removeClass('selected');
    $targetOption.addClass('selected');

    // Update visible text
    if ($display.length) {
        $display.text(nextTitle);
    }

    // Update hidden field
    if ($hiddenInput.length && nextCode) {
        $hiddenInput.val(nextCode);
    }

    if (customerTitle) {
        $dropdown.addClass('non-editable');
    }
}

/**
 * Resets the country dialing code dropdown in the contact form.
 *
 * - If a mobileDialingCode is provided, selects the matching country code.
 * - If not provided, selects the first available option.
 * - Updates the displayed country code and selected state.
 * - Resets the mobile input maxlength to default (10).
 * - Makes the dropdown non-editable when a dialing code is explicitly passed.
 *
 * @function resetCountryCodeToDefault
 * @param {string} [mobileDialingCode] - Optional country dialing code to preselect (e.g., "+91").
 * @returns {void}
 */
function resetCountryCodeToDefault(mobileDialingCode) {
    var countryCode = mobileDialingCode || '';
    var $container = $('.contactusform-mobile-dialing-code');
    var $defaultOption;
    $mobile.attr('maxlength', 10); // reset maxlength to default 10

    // If countryCode is passed → use it
    if (countryCode) {
        $defaultOption = $container
            .find('.custom-option-value[data-id="' + countryCode + '"]')
            .first();
    } else { // Else → pick FIRST option from the list
        $defaultOption = $container.find('.custom-option-value').first();
    }

    if ($defaultOption.length) {
        var defaultCode = $defaultOption.data('id');

        // Update display
        $container.find('.selected-value, .country-selected-value')
            .text(defaultCode)
            .addClass('active');

        // Reset selected states
        $container.find('.custom-option-value, .country-option-item')
            .removeClass('selected');

        $defaultOption.addClass('selected');
        $container.addClass('selected');

        // Hide dropdown
        $container.find('.custom-option-values, .country-dropdown-options').hide();
    }

    if (mobileDialingCode) {
        var phoneLength = $('#contactUsForm .custom-option-value.custom-option-login.selected').data('phonelength');
        var $mobileInput = $('#contactUsForm #mobile');
        $mobileInput.attr('maxlength', phoneLength);
        $mobileInput.attr('data-mobile-dialing-code', mobileDialingCode);
        $container.find('.custom-drop-down-section').addClass('non-editable');
    }
}

/**
 * Fetches logged-in customer details from the server
 * and pre-fills the Contact Us form fields.
 * Fields are set to readonly when customer data exists.
 *
 * @function loadCustomerDetails
 * @returns {void}
 */
function loadCustomerDetails() {
    var CustomerDetailsUrl = $('.contact-us-form-wrapper').data('url');
    $.ajax({
        url: CustomerDetailsUrl,
        method: 'GET',
        success: function (data) {
            if (data && data.customer) {
                var customer = data.customer;
                if (customer.customerTitle) resetCustomerTitle(customer.customerTitle);
                $firstName.val(customer.firstName).prop('readonly', true).siblings('.placeholder').addClass('d-none');
                $lastName.val(customer.lastName).prop('readonly', true).siblings('.placeholder').addClass('d-none');
                $email.val(customer.customerEmail).prop('readonly', true).siblings('.placeholder').addClass('d-none');
                if (customer.mobileDialingCode) resetCountryCodeToDefault(customer.mobileDialingCode);
                $mobile.val(customer.customerPhone).prop('readonly', true).siblings('.placeholder').addClass('d-none');
            }
        },
        error: function () {
            toastMessageHelper.showNotification('Please try again later.');
        }
    });
}

// Clear phone input when country code changes
$('body').on('click', '.contactusform-mobile-dialing-code .custom-option-value.custom-option-login', function () {
    $mobile.val(''); // clear phone number
    var $mobileNumberGroup = $('.mobile-number-group');
    $('.contactusform-mobile-placeholder').removeClass('d-none');
    $mobileNumberGroup.find('.custom-drop-down-section').removeClass('is-invalid');
    $('#mobile').removeClass('is-invalid');
    $mobileNumberGroup.find('.invalid-feedback').text('');
});

var contactusformCustomerTitle = $('#contactusform-customer-title');
var contactusformMobileDialingCode = $('#contactusform-mobile-dialing-code');

var customerTitleUrl = contactusformCustomerTitle.data('url');
var mobileDialingCodeUrl = contactusformMobileDialingCode.data('url');

// Load Customer Title options
if (contactusformCustomerTitle.length && customerTitleUrl) {
    $.ajax({
        url: customerTitleUrl,
        method: 'GET',
        success: function (data) {
            contactusformCustomerTitle.html(data);
        }
    });
}

// Load country dialing code options
if (contactusformMobileDialingCode.length && mobileDialingCodeUrl) {
    $.ajax({
        url: mobileDialingCodeUrl,
        method: 'GET',
        success: function (data) {
            contactusformMobileDialingCode.html(data);
        }
    });
}

if ($('.isLoggedInUser').length !== 0 && $('.isLoggedInUser').val() === 'true') {
    loadCustomerDetails();
}

/**
 * Handles form submission event.
 * Prevents default behavior, validates fields, and shows success or error messages.
 */
$form.on('submit', function (e) {
    e.preventDefault();

    // Reset previous errors
    $('.form-control').removeClass('is-invalid');
    $('.invalid-feedback').text('');
    // Run validations
    const isFirstNameValid = validateName($firstName, 'First');
    const isLastNameValid = validateName($lastName, 'Last');
    const isEmailValid = validateEmail($email);
    const isMobileValid = validateMobile($mobile);
    const isMessageValid = validateMessage($message);
    var title = $('#contactUsForm .contactusform-customer-title-dropdown .selected-value').text().trim();
    var countryCode = $('#contactUsForm .contactusform-mobile-dialing-code .selected-value').text().trim();

    const isValid = isFirstNameValid && isLastNameValid && isEmailValid && isMobileValid && isMessageValid;

    if (isValid) {
        var url = $('#pagecontactus').val();
        var formData = $(this).serialize();

        formData += '&title=' + encodeURIComponent(title);
        formData += '&countryCode=' + encodeURIComponent(countryCode);

        $.spinner().start();
        $.ajax({
            url: url,
            type: 'post',
            dataType: 'json',
            data: formData,
            success: function (data) {
                $form[0].reset();
                $('.form-control').removeClass('is-invalid');
                $('.invalid-feedback').text('');
                $('.placeholder').removeClass('d-none');
                $('#contactUsModal').modal('hide');
                $.spinner().stop();
                if (data.success === true) {
                    var successMsg = $('#successMsg').val();
                    $('#successModal .message').text(successMsg);
                } else {
                    var duplicateMsg = $('#duplicateMsg').val();
                    $('#successModal .message').text(duplicateMsg);
                }
                $('#successModal').modal('show');

                if ($('.isLoggedInUser').length !== 0 && $('.isLoggedInUser').val() === 'true') {
                    loadCustomerDetails();
                } else {
                    resetCustomerTitle();
                    resetCountryCodeToDefault();
                }
            },
            error: function () {
                $.spinner().stop();
            }
        });
    }
});

// Live validation handlers

/**
 * Restricts name inputs to letters only and validates in real-time.
 */
$firstName.add($lastName).on('input', function () {
    // // Allow only letters and spaces
    // this.value = this.value.replace(/[^a-zA-Z\s]/g, '');

    // // Remove leading spaces only
    // this.value = this.value.replace(/^\s+/, '');

    const $field = $(this);
    const label = this.id === 'firstName' ? 'First' : 'Last';
    validateName($field, label);

    // Allow only letters and spaces
    this.value = this.value.replace(/[^a-zA-Z\s]/g, '');

    // Replace multiple spaces with a single space
    this.value = this.value.replace(/\s+/g, ' ');

    // Remove leading space
    this.value = this.value.replace(/^\s/, '');

    // if (this.value) {
    //     clearError($(this));
    //     removeInvalidFromCustomerTitle();
    // }
});

/**
 * Validates email in real-time as user types.
 */
$email.on('input', function () {
    validateEmail($(this));
});

/**
 * Restricts mobile input to digits only and validates in real-time.
 */
$mobile.on('input', function () {
    this.value = this.value.replace(/[^0-9]/g, '');
    validateMobile($(this));
});

/**
 * Validates message field in real-time based on word count.
 */
$message.on('input blur', function () {
    validateMessage($(this));
});

/**
 * Resets all input fields and clears errors when the Contact Us modal is closed.
 */
$('#contactUsModal').on('hidden.bs.modal', function () {
    // Reset form values
    $form[0].reset();

    // Clear all validation errors
    $('.form-control').removeClass('is-invalid');
    $('.invalid-feedback').text('');

    // Reset placeholders (if you hide them dynamically)
    $('.placeholder').removeClass('d-none');
});

// Validate first name on blur
$firstName.on('blur', function () {
    validateName($(this), 'First');
});

// Validate last name on blur
$lastName.on('blur', function () {
    validateName($(this), 'Last');
});

// Validate email on blur
$email.on('blur', function () {
    validateEmail($(this));
});

// Validate mobile on blur
$mobile.on('blur', function () {
    this.value = this.value.replace(/[^0-9]/g, '');
    validateMobile($(this));
});

$('body').on('click', '#contactUsForm .phone-input-field .custom-option-values .custom-option-value', function () {
    var maxLength = $(this).attr('data-phonelength') || 10;
    $mobile.attr('maxlength', maxLength);
    $mobile.attr('data-mobile-dialing-code', $(this).attr('data-id'));
});
